/* eslint-disable react/button-has-type,react/prop-types */
import type { ButtonHTMLAttributes } from 'react';
import React from 'react';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import type { BadgeSize } from '../../components/Badge/Badge';
import type { ShortcutKey } from '../../formatting/Shortcut/Shortcut';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import type { ControlSize, ControlVariant } from '../shared/types';
import { Icon } from '../../assets/Icon/Icon';
import { LoadingIcon } from '../../assets/LoadingIcon/LoadingIcon';
import { useControlSize } from '../../common/control_size';
import { useControlVariant } from '../../common/control_variant';
import { Badge } from '../../components/Badge/Badge';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import Shortcut from '../../formatting/Shortcut/Shortcut';
import { colors, darkThemeSelector, fontWeights, shadows, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import {
  AccessLevel,
  isAccessLevelVisible,
  useAccessLevelContext,
} from '../../utilities/useAccessLevelContext';
import { useViewport } from '../../utilities/useViewport';
import { selectors, transitions } from '../shared/styles';
import {
  destructiveMutedDisabledStyles,
  destructiveMutedEnabledStyles,
  destructiveVividDisabledStyles,
  destructiveVividEnabledStyles,
  iconColor,
  internalDisabledStyles,
  internalEnabledStyles,
  labelColor,
  loadingIconColor,
  primaryMutedDisabledStyles,
  primaryMutedEnabledStyles,
  primaryVividDisabledStyles,
  primaryVividEnabledStyles,
  secondaryDisabledStyles,
  secondaryEnabledStyles,
} from './ButtonStyles';

const ButtonIcon = styled(Icon, {
  opacity: 1,
  width: '$$iconSize',
  height: '$$iconSize',
  color: iconColor,
  transition: transitions.control,

  variants: {
    isLoading: {
      true: {
        opacity: 0,
      },
      false: {},
    },
  },
});

const ButtonLoadingIcon = styled(LoadingIcon, {
  width: '$$loadingSize',
  height: '$$loadingSize',
  color: loadingIconColor,
  transition: transitions.control,
});

const ButtonLabel = styled(Text, {
  display: 'flex',
  fontWeight: fontWeights.bold,
  transition: transitions.control,
  // NOTE: Override base Text colors
  color: `${labelColor} !important`,

  [darkThemeSelector]: {
    color: `${labelColor} !important`,
  },

  variants: {
    isLoading: {
      true: {
        opacity: 0,
      },
      false: {
        opacity: 1,
      },
    },
    arrangement: {
      'leading-icon': {},
      'leading-label': {},
    },
    condense: {
      true: {},
      false: {},
    },
    stretch: {
      true: {
        width: '100%',
      },
      false: {},
    },
    size: {
      'x-small': {
        ...SmallSansSizes,
      },
      small: {
        ...SmallSansSizes,
      },
      medium: {
        ...BodySansSizes,
      },
      large: {
        ...LargeSansSizes,
      },
      'x-large': {
        ...LargeSansSizes,
      },
    },
  },
});

export const ButtonContainer = styled('button', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '$$buttonGap',
  minWidth: 'min-content',
  paddingX: '$$paddingX',
  paddingY: '$$paddingY',
  opacity: 1,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  transition: transitions.control,

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    appearance: {
      muted: {},
      vivid: {},
    },
    arrangement: {
      'hidden-label': {},
      'leading-icon': {},
      'leading-label': {},
    },
    hasMenu: {
      true: {},
      false: {},
    },
    size: {
      'x-small': {
        heightAll: '$20',
        borderRadius: '$6',

        $$buttonGap: '$space$2',
        $$paddingX: '$space$4',
        $$paddingY: '$space$2',
        $$iconSize: '$space$12',
        $$loadingSize: '$space$14',
      },
      small: {
        '@notDesktop': {
          heightAll: '$28',
          borderRadius: '$10',

          $$buttonGap: '$space$6',
          $$paddingX: '$space$8',
          $$paddingY: '$space$2',
          $$iconSize: '$space$14',
          $$loadingSize: '$space$16',
        },

        '@desktop': {
          heightAll: '$24',
          borderRadius: '$8',

          $$buttonGap: '$space$4',
          $$paddingX: '$space$6',
          $$paddingY: '$space$2',
          $$iconSize: '$space$12',
          $$loadingSize: '$space$14',
        },
      },
      medium: {
        '@notDesktop': {
          heightAll: '$32',
          borderRadius: '$10',

          $$buttonGap: '$space$6',
          $$paddingX: '$space$10',
          $$paddingY: '$space$4',
          $$iconSize: '$space$14',
          $$loadingSize: '$space$16',
        },

        '@desktop': {
          heightAll: '$28',
          borderRadius: '$8',

          $$buttonGap: '$space$4',
          $$paddingX: '$space$8',
          $$paddingY: '$space$4',
          $$iconSize: '$space$12',
          $$loadingSize: '$space$14',
        },
      },
      large: {
        heightAll: '$36',
        borderRadius: '$10',

        $$buttonGap: '$space$6',
        $$paddingX: '$space$12',
        $$paddingY: '$space$4',
        $$iconSize: '$space$12',
        $$loadingSize: '$space$14',
      },
      'x-large': {
        heightAll: '$44',
        borderRadius: '$12',

        $$buttonGap: '$space$8',
        $$paddingX: '$space$20',
        $$paddingY: '$space$6',
        $$iconSize: '$space$12',
        $$loadingSize: '$space$14',
      },
    },
    internal: {
      true: {},
      false: {},
    },
    isDisabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
      false: {},
    },
    ghost: {
      true: {},
      false: {},
    },
    invalid: {
      true: {},
      false: {},
    },
    variant: {
      destructive: {},
      primary: {},
      secondary: {},
    },
  },
  compoundVariants: [
    // Icon only
    {
      arrangement: 'hidden-label',
      size: 'x-small',
      hasMenu: false,
      css: {
        minWidth: '$24',
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'small',
      hasMenu: false,
      css: {
        minWidth: '$28',
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'medium',
      hasMenu: false,
      css: {
        minWidth: '$32',
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'large',
      hasMenu: false,
      css: {
        minWidth: '$40',
      },
    },
    {
      arrangement: 'hidden-label',
      size: 'x-large',
      hasMenu: false,
      css: {
        minWidth: '$48',
      },
    },
    // Destructive
    {
      appearance: 'vivid',
      variant: 'destructive',
      isDisabled: false,
      css: {
        '&[type=button], &[type=submit]': destructiveVividEnabledStyles,
        '&': destructiveVividEnabledStyles,
      },
    },
    {
      appearance: 'vivid',
      variant: 'destructive',
      isDisabled: true,
      css: destructiveVividDisabledStyles,
    },

    {
      appearance: 'muted',
      variant: 'destructive',
      isDisabled: false,
      css: destructiveMutedEnabledStyles,
    },

    {
      appearance: 'muted',
      variant: 'destructive',
      isDisabled: true,
      css: destructiveMutedDisabledStyles,
    },

    // Primary
    {
      appearance: 'vivid',
      variant: 'primary',
      isDisabled: false,
      css: {
        '&[type=button], &[type=submit]': primaryVividEnabledStyles,
        '&': primaryVividEnabledStyles,
      },
    },
    {
      appearance: 'vivid',
      variant: 'primary',
      isDisabled: true,
      css: primaryVividDisabledStyles,
    },

    {
      appearance: 'muted',
      variant: 'primary',
      isDisabled: false,
      css: primaryMutedEnabledStyles,
    },
    {
      appearance: 'muted',
      variant: 'primary',
      isDisabled: true,
      css: primaryMutedDisabledStyles,
    },

    // Secondary
    {
      variant: 'secondary',
      isDisabled: false,
      css: secondaryEnabledStyles,
    },
    {
      variant: 'secondary',
      isDisabled: true,
      css: secondaryDisabledStyles,
    },

    // Internal
    {
      internal: true,
      isDisabled: false,
      css: internalEnabledStyles,
    },
    {
      internal: true,
      isDisabled: true,
      css: internalDisabledStyles,
    },

    {
      isDisabled: false,
      invalid: true,
      css: {
        boxShadow: shadows.fieldErrorLight,

        '&:hover:not(:focus)': {
          boxShadow: shadows.fieldErrorLight,
        },

        [darkThemeSelector]: {
          boxShadow: shadows.fieldErrorDark,

          '&:hover:not(:focus)': {
            boxShadow: shadows.fieldErrorDark,
          },
        },
      },
    },

    // Ghost
    {
      ghost: true,
      css: {
        '&[data-ghost="true"]:not(:hover, :focus, :focus-visible, :focus-within)': {
          backgroundColor: colors.transparent,
          boxShadow: colors.transparent,
        },
      },
    },
  ],
});

const ButtonLoadingContainer = styled('div', {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ButtonRow = styled(AlignStack, {
  gap: '$$buttonGap',
  width: 'fit-content',

  variants: {
    stretch: {
      true: {
        width: '100%',
      },
      false: {},
    },
  },
});

const ButtonMenuArrow = styled(ButtonIcon, {
  height: '$10',
  width: '$10',
});

export type ButtonAppearanceProp = 'muted' | 'vivid';
export type ButtonArrangementProp = 'hidden-label' | 'leading-icon' | 'leading-label';
export type ButtonMenuArrowProp = 'dropdown' | 'select';
export type ButtonLabelAlignProp = 'start' | 'center' | 'end';
export type ButtonSizeProp = ControlSize;
export type ButtonTypeProp = 'button' | 'submit' | 'reset';
export type ButtonVariantProp = ControlVariant;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Additional annotation such a result count within the button.
   * */
  annotation?: React.ReactNode;
  /**
   * The level required to access the button.
   */
  accessLevel?: AccessLevel;
  /**
   * Determines if the button is muted (more flat, simple colors) or vivid (more pronounced, colorful).
   */
  appearance?: ButtonAppearanceProp;
  /**
   * Determines the layout of the button's content.
   */
  arrangement?: ButtonArrangementProp;
  /**
   * The content of the button.
   */
  children: React.ReactNode;
  /**
   * Hides the button's label for smaller viewports.
   */
  condense?: boolean;
  /**
   * Disables the button.
   */
  disabled?: boolean;
  /**
   * Marks the button as invalid.
   */
  invalid?: boolean;
  /**
   * Set the form attribute.
   */
  form?: string;
  /**
   * Set which icon to display, no value displays no icon.
   */
  icon?: IconName;
  /**
   * Marks the button as internal.
   */
  internal?: boolean;
  /**
   * Hides the button's background and stroke until interacted with.
   */
  ghost?: boolean;
  /**
   * Displays a dropdown arrow for menus.
   */
  menuArrow?: ButtonMenuArrowProp;
  /**
   * Set the alignment of the button's label.
   */
  labelAlign?: ButtonLabelAlignProp;
  /**
   * Stretches the button's label to fill the width of the button.
   */
  labelStretch?: boolean;
  /**
   * Displays a loading icon.
   */
  loading?: boolean;
  /**
   * Set shortcut keys for the button.
   */
  shortcut?: ShortcutKey[];
  /**
   * Shorter label for the button.
   */
  shortLabel?: React.ReactNode;
  /**
   * Set the size of the button.
   */
  size?: ButtonSizeProp;
  /**
   * Set the type of the button.
   */
  type?: ButtonTypeProp;
  /**
   * Set the most appropriate variant of the component for your use.
   */
  variant?: ButtonVariantProp;
  /**
   * Set a width.
   */
  width?: string;
  /**
   * Set a maximum width.
   */
  maxWidth?: string;
  /**
   * Set a minimum width.
   */
  minWidth?: string;
}

const getButtonPreset = (size: ButtonSizeProp) => {
  if (size === 'x-small' || size === 'small') return 'small';
  if (size === 'x-large' || size === 'large') return 'large';
  return 'body';
};

export function Button<Tag extends React.ElementType>({
  annotation,
  appearance = 'vivid',
  'aria-label': ariaLabel,
  arrangement = 'leading-label',
  children,
  condense,
  disabled = false,
  ghost,
  icon,
  accessLevel: accessLevelProp,
  internal,
  invalid = false,
  labelAlign,
  labelStretch = false,
  loading = false,
  maxWidth,
  menuArrow,
  minWidth,
  shortcut,
  shortLabel,
  size,
  type = 'button',
  variant,
  width,
  ref,
  ...rest
}: PolymorphicComponentProps<Tag, ButtonProps>) {
  const { breakpoint } = useViewport();
  const accessLevel = useAccessLevelContext(accessLevelProp);

  const controlSize = useControlSize(size, 'medium');
  let generalSize;
  if (controlSize === 'x-large') {
    generalSize = 'large';
  } else if (controlSize === 'large') {
    generalSize = 'medium';
  } else if (controlSize === 'medium') {
    generalSize = 'small';
  } else {
    generalSize = 'x-small';
  }

  const controlVariant = useControlVariant(variant, 'primary');
  let generalVariant;
  if (controlVariant === 'secondary') {
    generalVariant = 'neutral';
  } else if (controlVariant === 'destructive') {
    generalVariant = 'negative';
  } else {
    generalVariant = 'brand';
  }

  // Disable the button during its loading state
  const isDisabled = disabled || loading;

  const hiddenLabel = (condense && breakpoint !== 'desktop') || arrangement === 'hidden-label';

  const buttonRender = (
    <ButtonContainer
      {...rest}
      type={type}
      ref={ref}
      aria-label={
        ariaLabel || (arrangement === 'hidden-label' || condense ? `${children}` : undefined)
      }
      data-ghost={ghost}
      appearance={ghost ? 'muted' : appearance}
      arrangement={arrangement}
      variant={controlVariant}
      size={controlSize}
      internal={internal || accessLevel === AccessLevel.Internal}
      ghost={ghost}
      isDisabled={isDisabled}
      hasMenu={Boolean(menuArrow)}
      disabled={isDisabled}
      invalid={invalid}
      style={{
        width,
        maxWidth,
        minWidth,
      }}
    >
      {loading && (
        <ButtonLoadingContainer>
          <ButtonLoadingIcon />
        </ButtonLoadingContainer>
      )}
      <ButtonRow
        direction="row"
        justify="center"
        preset={getButtonPreset(controlSize)}
        end={
          (shortcut || menuArrow) && (
            <>
              {shortcut && (
                <Shortcut
                  control={appearance !== 'muted'}
                  internal={internal}
                  variant={controlVariant === 'secondary' ? 'neutral' : controlVariant}
                  keys={shortcut}
                />
              )}
              {menuArrow && (
                <ButtonMenuArrow
                  isLoading={loading}
                  icon={menuArrow === 'dropdown' ? 'chevron-down' : 'chevrons-vertical'}
                />
              )}
            </>
          )
        }
        width="full"
      >
        <ButtonRow
          direction={
            arrangement === 'leading-icon' || arrangement === 'hidden-label' ? 'row' : 'row-reverse'
          }
          justify="center"
          preset={getButtonPreset(controlSize)}
          start={icon && <ButtonIcon isLoading={loading} icon={icon} />}
          end={
            Boolean((accessLevel && isAccessLevelVisible(accessLevel)) || annotation) && (
              <>
                {accessLevel && isAccessLevelVisible(accessLevel) && (
                  <FeatureBadge size="x-small" arrangement="icon-only" type={accessLevel} />
                )}
                {annotation && (
                  <Badge
                    internal={internal}
                    size={generalSize as BadgeSize}
                    variant={generalVariant as VariantProp}
                  >
                    {annotation}
                  </Badge>
                )}
              </>
            )
          }
          stretch={labelStretch}
          width="full"
        >
          {!hiddenLabel && (
            <ButtonLabel isLoading={loading} size={controlSize} stretch={labelStretch}>
              {breakpoint !== 'desktop' && shortLabel ? shortLabel : children}
            </ButtonLabel>
          )}
        </ButtonRow>
      </ButtonRow>
    </ButtonContainer>
  );

  if (arrangement === 'hidden-label' || (condense && breakpoint !== 'desktop')) {
    return (
      <Tooltip contents={children} sideOffset={2}>
        {buttonRender}
      </Tooltip>
    );
  }

  return buttonRender;
}
